import { scoreColor } from "@/lib/format";
import { Sparkles } from "lucide-react";

// Radar-scope compliance gauge: black/green airplane-radar aesthetic with a rotating sweep,
// concentric range rings, crosshair and a phosphor-green readout.
// A PERFECT, fully-clean report (score 100 with zero flags) gets a celebratory gold treatment.
export const ScoreGauge = ({ score, flawless = false }) => {
  const hasScore = Number.isFinite(score);
  const safeScore = hasScore ? score : 0;
  const perfect = flawless && hasScore;
  const size = 150;
  const radius = size / 2;
  const stroke = 8;
  const normalized = radius - stroke / 2 - 6;
  const circumference = normalized * 2 * Math.PI;
  const offset = circumference - (safeScore / 100) * circumference;
  const risk = hasScore ? scoreColor(safeScore) : "#94A3B8";
  const accent = perfect ? "#FBBF24" : risk; // gold when flawless
  const ringTint = perfect ? "rgba(251,191,36,0.32)" : "rgba(52,211,153,0.30)";
  const verdict = !hasScore
    ? "No signal"
    : perfect
      ? "Perfect — All Clear"
      : safeScore >= 90 ? "Largely Compliant" : safeScore >= 70 ? "Needs Attention" : "High Risk";

  return (
    <div data-testid="compliance-score" data-perfect={perfect} className="flex flex-col items-center">
      <div
        className={`relative overflow-hidden rounded-full ${perfect ? "animate-perfect-glow" : ""}`}
        style={{
          width: size,
          height: size,
          background: "radial-gradient(circle at 50% 50%, #063a28 0%, #052617 45%, #020e09 100%)",
          ...(perfect
            ? {}
            : { boxShadow: "inset 0 0 24px rgba(0,0,0,0.8), inset 0 0 12px rgba(52,211,153,0.25), 0 0 0 2px rgba(52,211,153,0.35)" }),
        }}
      >
        {/* rotating sweep beam */}
        <div
          className="absolute inset-0 rounded-full animate-radar-spin"
          style={{
            background: perfect
              ? "conic-gradient(from 0deg, rgba(251,191,36,0) 0deg, rgba(251,191,36,0.08) 20deg, rgba(251,191,36,0.40) 60deg, rgba(251,191,36,0) 72deg)"
              : "conic-gradient(from 0deg, rgba(52,211,153,0) 0deg, rgba(52,211,153,0.06) 20deg, rgba(52,211,153,0.35) 60deg, rgba(52,211,153,0) 72deg)",
          }}
          aria-hidden="true"
        />

        <svg height={size} width={size} className="absolute inset-0 -rotate-90">
          {/* range rings */}
          {[normalized, normalized * 0.68, normalized * 0.36].map((r) => (
            <circle key={r} stroke={ringTint} fill="transparent" strokeWidth="1" r={r} cx={radius} cy={radius} />
          ))}
          {/* crosshair */}
          <line x1={radius} y1={6} x2={radius} y2={size - 6} stroke={ringTint} strokeWidth="1" />
          <line x1={6} y1={radius} x2={size - 6} y2={radius} stroke={ringTint} strokeWidth="1" />
          {/* score arc (gold when flawless, else coloured by risk) */}
          <circle
            stroke={accent}
            fill="transparent"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${circumference} ${circumference}`}
            style={{ strokeDashoffset: offset, transition: "stroke-dashoffset 1s ease", filter: `drop-shadow(0 0 ${perfect ? 9 : 6}px ${accent})` }}
            r={normalized}
            cx={radius}
            cy={radius}
          />
        </svg>

        {/* CRT scanlines */}
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{ background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 1px, transparent 2px, transparent 4px)" }}
          aria-hidden="true"
        />

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {perfect && (
            <Sparkles
              className="mb-0.5 h-4 w-4 animate-perfect-pop"
              style={{ color: accent, filter: `drop-shadow(0 0 6px ${accent})` }}
              aria-hidden="true"
            />
          )}
          <span
            className={`font-mono-plex text-4xl font-700 tracking-tight ${perfect ? "animate-perfect-pop" : ""}`}
            style={{ color: accent, textShadow: `0 0 ${perfect ? 14 : 10}px ${accent}` }}
          >
            {hasScore ? safeScore : "—"}
          </span>
        </div>
      </div>

      <div
        className="mt-3 flex items-center gap-1.5 font-mono-plex text-xs font-600 uppercase tracking-[0.15em]"
        style={{ color: accent }}
      >
        {perfect && <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
        {verdict}
      </div>
    </div>
  );
};
